/**
 * AnalysisCoverageBlock.js
 * Reusable Analysis Coverage block: what was analyzed, skipped/limited, and how trustworthy.
 * Uses only repo.analysisCoverage (no new API). Rendered in RepoAnalyzer, SystemOverview, FeaturePlansPanel.
 */
import React from "react";
import "./AnalysisCoverageBlock.css";

const UNKNOWN = "Unknown";
const DASH = "—";

function formatValue(value) {
  if (value === null || value === undefined) return UNKNOWN;
  return value;
}

function formatPrimaryLanguages(primaryLanguages) {
  if (!primaryLanguages || !Array.isArray(primaryLanguages) || primaryLanguages.length === 0) return DASH;
  return primaryLanguages
    .slice(0, 5)
    .map((p) => {
      const lang = typeof p === "string" ? p : (p && p.language);
      if (!lang) return null;
      const percent = p && typeof p === "object" && p.percent != null ? ` (${p.percent}%)` : "";
      return `${lang}${percent}`;
    })
    .filter(Boolean)
    .join(", ");
}

function AnalysisCoverageBlock({ repo, title = "Analysis Coverage" }) {
  const ac = repo?.analysisCoverage;
  if (!repo) return null;

  const eligibleFileCount = ac?.eligibleFileCount;
  const selectedFileCount =
    ac?.selectedFileCount ?? (Array.isArray(ac?.selectedFilePaths) ? ac.selectedFilePaths.length : undefined);
  const processedFileCount = ac?.processedFileCount;
  const failedFileCount = ac?.failedFileCount;
  const fileCapApplied = ac?.fileCapApplied;
  const chunkFileCount = ac?.chunkFileCount;
  const chunkMatchesSummarySet = ac?.chunkMatchesSummarySet;
  const coverageIntegrity = ac?.coverageIntegrity;
  const indexedVectorCountCode = ac?.indexedVectorCountCode;
  const indexedVectorCountDoc = ac?.indexedVectorCountDoc;
  const primaryLanguages = formatPrimaryLanguages(ac?.deterministicCoverage?.primaryLanguages);
  const tier = ac?.deterministicCoverage?.tier;

  const hasMismatchOrFail =
    chunkMatchesSummarySet === false || coverageIntegrity === "FAIL";
  const hasWarnOrCap = coverageIntegrity === "WARN" || fileCapApplied === true;
  const tierLow = tier === "Low";

  const selectedDisplay = selectedFileCount != null ? selectedFileCount : UNKNOWN;
  const processedDisplay = formatValue(processedFileCount);
  const failedDisplay = formatValue(failedFileCount);
  const capDisplay = fileCapApplied === true ? "Yes" : fileCapApplied === false ? "No" : UNKNOWN;
  const indexedFilesDisplay = formatValue(chunkFileCount);
  const matchDisplay =
    chunkMatchesSummarySet === true ? "Yes" : chunkMatchesSummarySet === false ? "No" : UNKNOWN;
  const integrityDisplay =
    coverageIntegrity === "OK" || coverageIntegrity === "WARN" || coverageIntegrity === "FAIL"
      ? coverageIntegrity
      : UNKNOWN;
  const codeVectorsDisplay = formatValue(indexedVectorCountCode);
  const docVectorsDisplay = formatValue(indexedVectorCountDoc);
  const tierDisplay =
    tier === "High" || tier === "Medium" || tier === "Low" ? tier : DASH;

  const indicatorClass = hasMismatchOrFail ? "coverage-indicator bad" : hasWarnOrCap ? "coverage-indicator warn" : "";
  return (
    <div
      className={`analysis-coverage-block ${indicatorClass}`.trim()}
      title="Summarized vs indexed file sets"
    >
      <h3 className="analysis-coverage-block-title">{title}</h3>
      <div className="analysis-coverage-block-content">
        <div className="coverage-row">
          <span className="coverage-label">Eligible files:</span> {formatValue(eligibleFileCount)}
        </div>
        <div className="coverage-row">
          <span className="coverage-label">Selected for analysis:</span> {selectedDisplay}
        </div>
        <div className="coverage-row">
          <span className="coverage-label">Processed:</span> {processedDisplay}
        </div>
        <div className="coverage-row">
          <span className="coverage-label">Failed:</span> {failedDisplay}
        </div>
        <div className="coverage-row">
          <span className="coverage-label">Cap applied:</span> {capDisplay}
        </div>
        <div className="coverage-row">
          <span className="coverage-label">Indexed files:</span> {indexedFilesDisplay}
        </div>
        <div className={`coverage-row ${chunkMatchesSummarySet === false ? "coverage-indicator bad" : ""}`}>
          <span className="coverage-label">Indexed files match summaries:</span>{" "}
          {matchDisplay}
          {chunkMatchesSummarySet === true && <span className="coverage-check" aria-hidden> ✓</span>}
          {chunkMatchesSummarySet === false && <span className="coverage-warn-icon" aria-hidden> ✗</span>}
        </div>
        <div className="coverage-row">
          <span className="coverage-label">Integrity:</span>{" "}
          <span
            className={
              integrityDisplay === "OK"
                ? "coverage-badge ok"
                : integrityDisplay === "WARN"
                  ? "coverage-badge warn"
                  : integrityDisplay === "FAIL"
                    ? "coverage-badge fail"
                    : ""
            }
          >
            {integrityDisplay}
          </span>
        </div>
        <div className="coverage-row">
          <span className="coverage-label">Code vectors:</span> {codeVectorsDisplay}
        </div>
        <div className="coverage-row">
          <span className="coverage-label">Doc vectors:</span> {docVectorsDisplay}
        </div>
        <p className="coverage-vectors-hint">Vectors in index (from latest analysis).</p>
        <div className="coverage-row">
          <span className="coverage-label">Primary languages:</span>{" "}
          {primaryLanguages || UNKNOWN}
        </div>
        <div className={`coverage-row ${tierLow ? "coverage-tier-low" : ""}`}>
          <span className="coverage-label">Deterministic tier:</span>{" "}
          {tier === "High" || tier === "Medium" || tier === "Low" ? (
            <span className={`tier-badge tier-${tier.toLowerCase()}`}>{tierDisplay}</span>
          ) : (
            tierDisplay
          )}
          {tierLow && <span className="coverage-caution-badge" aria-hidden> Caution</span>}
        </div>
      </div>
    </div>
  );
}

export default AnalysisCoverageBlock;
